import React from "react";
import { RiskStatistics } from "@sscs/report/ui";
import { ReportDetails } from "@sscs/types";
import { DataTable, Table, ValueOrPlaceholder } from "@sscs/ui";
import { getReleaseDateDisplay } from "@sscs/utils";
import {
  ApprovalStatus,
  DisplaySize,
  FileStatus,
  LinkToReport,
} from "Components";
import { Version } from "Types";
import { useVersionReportSummary } from "../../hooks";
import VersionActions from "./VersionActions";
import VersionInfo from "./VersionInfo/VersionInfo";

type VersionSummaryProps = {
  rowIndex: number;
  version: Version;
  predecessorVersion: string | undefined;
  inCatalog: boolean;
  attachedToExternalCatalog: boolean;
  isOnlyApprovedVersion: boolean;
  onDerive: (predecessor: Version) => void;
};

const VersionSummary = ({
  rowIndex,
  version,
  predecessorVersion,
  inCatalog,
  attachedToExternalCatalog,
  isOnlyApprovedVersion,
  onDerive,
}: VersionSummaryProps): React.JSX.Element => {
  const { reportSummary, showReproReport } = useVersionReportSummary(version);

  const currentStatus =
    version.artifacts_summary.version.current_status.code !== "DONE" ||
    !version.artifacts_summary.repro
      ? version.artifacts_summary.version.current_status
      : version.artifacts_summary.repro.current_status;
  const reportDetails: ReportDetails = {
    projectName: version.project_name,
    packageName: version.package_name,
    version: version.software_info.version,
    isRepro: showReproReport,
  };

  return (
    <>
      <DataTable.ExpandedRow.Summary>
        <Table.Cell>
          <FileStatus
            status={currentStatus}
            scanReport={reportSummary?.scan_report}
            scanDuration={reportSummary?.scan_duration}
          />
        </Table.Cell>
        <Table.Cell>
          <ApprovalStatus
            status={version.approval_status}
            info={version.approval_information}
          />
        </Table.Cell>
        <Table.Cell>
          <VersionInfo
            rowIndex={rowIndex}
            version={version}
            predecessorVersion={predecessorVersion}
            inCatalog={inCatalog}
            attachedToExternalCatalog={attachedToExternalCatalog}
            onUploadNext={() => onDerive(version)}
          />
        </Table.Cell>
        <Table.Cell>
          <RiskStatistics
            risk={reportSummary?.scan_report.assessments}
            riskCountWrapper={(assessment, category, children) => (
              <LinkToReport
                id={`${category}-link-${rowIndex}`}
                report={{
                  ...reportDetails,
                  riskCategory: category,
                  riskViolations: assessment.violations,
                }}
              >
                {children}
              </LinkToReport>
            )}
            viewReportWrapper={(children) => (
              <LinkToReport
                id={`view-report-link-${rowIndex}`}
                report={reportDetails}
              >
                {children}
              </LinkToReport>
            )}
          />
        </Table.Cell>
        <Table.Cell>
          <ValueOrPlaceholder
            value={
              version.is_released
                ? getReleaseDateDisplay(version.software_info.release_date)
                : null
            }
          />
        </Table.Cell>
        <Table.Cell>
          <DisplaySize sizeInBytes={version.usage} fractionDigits={0} />
        </Table.Cell>
        <Table.Cell align="center">
          <VersionActions
            id={`version-actions-${rowIndex}`}
            version={version}
            isDestructiveCatalogAction={inCatalog && isOnlyApprovedVersion}
          />
        </Table.Cell>
      </DataTable.ExpandedRow.Summary>
    </>
  );
};

export default VersionSummary;
